import { createInfiniteQuery } from '@funtech-int/rival';
import {
    SeasonWithEpisodes,
    seasonWithEpisodesQuery,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useInfinitePagination, useResources, useSignal } from '@funtech-int/rival-react';
import type { Source } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { GraphqlClient } from '@/shared/lib/graphqlClient';

import { useGraphqlClient } from './useGraphqlClient';

const EPISODES_LIMIT = 20;

const EPISODES_LIST_UPDATE_DELAY = 5 * 60 * 1000;

interface SeasonWithEpisodesParams {
    contentId: string;
    withUserData: boolean;
    source?: Source;
    billingFeaturesCount: number;
}

interface SeasonWithEpisodesQueryContext {
    params: SeasonWithEpisodesParams;
    client: GraphqlClient;
}

interface SeasonWithEpisodesPageInfo {
    offset: number;
    limit: number;
}

interface SeasonWithEpisodesMeta extends Omit<SeasonWithEpisodes, 'episodes'> {
    episodes: {
        total: number;
        items: SeasonWithEpisodes['episodes']['items'][number][];
    };
}

const seasonWithEpisodesInfiniteQuery = createInfiniteQuery<
    SeasonWithEpisodesQueryContext,
    SeasonWithEpisodesMeta | null,
    SeasonWithEpisodesPageInfo
>({
    key(ctx) {
        const keyParts = [
            seasonWithEpisodesQuery.name,
            ctx.params.contentId,
            String(ctx.params.billingFeaturesCount),
        ];

        return keyParts.filter(Boolean).join(':');
    },
    async fetch(ctx, prev, pageInfo) {
        const offset = pageInfo?.offset ?? 0;
        const limit = EPISODES_LIMIT;

        const { data } = await ctx.client.fetch({
            operation: seasonWithEpisodesQuery,
            variables: {
                contentId: ctx.params.contentId,
                withUserData: ctx.params.withUserData,
                offset,
                limit,
                source: ctx.params.source,
            },
        });

        if (!data || !data.episodes?.items?.length) {
            return prev ?? null;
        }

        const prevItems = prev?.episodes.items ?? [];

        return {
            ...data,
            episodes: {
                total: data.episodes.total,
                items: prevItems.concat(data.episodes.items),
            },
        };
    },
    nextPageInfo(data) {
        if (!data?.episodes || data.episodes.items.length >= data.episodes.total) {
            return undefined;
        }

        return {
            offset: data.episodes.items.length,
            limit: EPISODES_LIMIT,
        };
    },
    toJSON(data: any) {
        return data;
    },
    hydrate(data: any) {
        return seasonWithEpisodesQuery.hydrate(data) as any;
    },
});

interface UseSeasonWithEpisodesQueryParams {
    id: string;
    withUserData?: boolean;
    source?: Source;
}

export function useSeasonWithEpisodesQuery({
    id,
    withUserData = false,
    source,
}: UseSeasonWithEpisodesQueryParams) {
    const client = useGraphqlClient();

    const billingFeaturesCount = useSignal(useProfileManager().billingFeatureNames).length;

    const pagination = useInfinitePagination();

    const [season] = useResources(
        seasonWithEpisodesInfiniteQuery(
            {
                client,
                params: {
                    contentId: id,
                    withUserData,
                    source,
                    billingFeaturesCount,
                },
            },
            {
                pagination,
                lifetime: EPISODES_LIST_UPDATE_DELAY,
            }
        )
    );

    const hasMore = !!season?.episodes && season.episodes.items.length < season.episodes.total;

    return {
        season,
        pagination,
        hasMore,
    };
}
